/* eslint-disable */
/**
 * Public Blog Listing Page — /blog
 * Shows all published posts in a premium card grid.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Clock, ArrowRight, BookOpen, Tag } from 'lucide-react';
import { useBlog } from '../context/BlogContext';
import Navbar from '../components/landing-page/Navbar';

const CATEGORIES = ['Semua', 'Hukum Keluarga', 'Hukum Bisnis', 'Hukum Ketenagakerjaan', 'Hukum Perdata', 'Hukum Pidana'];

const COVER_COLORS = [
  'linear-gradient(135deg,#1a2d5a,#1e3a8a)',
  'linear-gradient(135deg,#0f3460,#16213e)',
  'linear-gradient(135deg,#1e3a6e,#0a1628)',
  'linear-gradient(135deg,#1a2d5a,#4c1d95)',
  'linear-gradient(135deg,#0f3460,#064e3b)',
];

export default function BlogPage() {
  const navigate = useNavigate();
  const { publishedPosts } = useBlog();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Semua');

  const filtered = publishedPosts.filter((p) => {
    const matchSearch =
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'Semua' || p.category === activeCategory;
    return matchSearch && matchCat;
  });

  const featuredPost = filtered[0];
  const restPosts = filtered.slice(1);

  return (
    <div className="min-h-screen bg-navy-950 font-sans selection:bg-gold-500/30">
      <Navbar />
      {/* Premium Ambient Background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-gold-500/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-900/20 blur-[150px]" />
        <div className="absolute top-[40%] left-[60%] w-[30%] h-[30%] rounded-full bg-indigo-900/10 blur-[100px]" />
      </div>

      {/* Hero Header */}
      <div className="relative z-10 pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gold-500/10 to-gold-500/5 border border-gold-500/20 text-gold-400 text-xs font-bold px-5 py-2 rounded-full mb-6 backdrop-blur-sm shadow-[0_0_20px_rgba(212,149,14,0.1)]">
              <BookOpen size={14} className="text-gold-400" />
              <span className="uppercase tracking-widest">Pusat Wawasan</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight leading-[1.1]">
              Insight &amp; Analisis{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-yellow-600">
                Hukum
              </span>
            </h1>
            <p className="text-navy-200 text-lg sm:text-xl max-w-2xl mx-auto font-light leading-relaxed">
              Panduan eksklusif, analisis kasus, dan pembaruan regulasi langsung dari para ahli hukum kami.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Search + Categories */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 mb-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/[0.02] border border-white/5 rounded-3xl p-2 sm:p-3 backdrop-blur-xl shadow-2xl flex flex-col md:flex-row gap-3 items-center"
        >
          <div className="relative w-full md:w-auto md:flex-1">
            <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-navy-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari artikel, topik, atau regulasi..."
              className="w-full bg-black/20 border border-white/5 focus:border-gold-500/30 text-white placeholder-navy-400/60 text-sm rounded-2xl pl-12 pr-4 py-4 outline-none transition-all shadow-inner"
            />
          </div>
          
          <div className="w-full md:w-auto flex overflow-x-auto [&::-webkit-scrollbar]:hidden gap-2 py-1 px-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-navy-950 shadow-[0_4px_15px_rgba(212,149,14,0.3)]'
                    : 'bg-white/5 hover:bg-white/10 text-navy-200 hover:text-white border border-white/5 hover:border-white/10'
                }`}
              >
                <Tag size={14} className={activeCategory === cat ? 'text-navy-950' : 'text-navy-400'} />
                {cat}
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Posts Section */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pb-32">
        {filtered.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-32 bg-white/[0.02] border border-white/5 rounded-3xl backdrop-blur-sm"
          >
            <div className="w-20 h-20 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner border border-white/5">
              <Search size={32} className="text-navy-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Tidak ada artikel ditemukan</h3>
            <p className="text-navy-400">Coba ubah kata kunci pencarian atau kategori Anda.</p>
          </motion.div>
        ) : (
          <>
            {/* Featured Post */}
            {featuredPost && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                onClick={() => navigate(`/blog/${featuredPost.slug}`)}
                className="group cursor-pointer mb-12 rounded-[2rem] overflow-hidden bg-white/[0.02] border border-white/10 hover:border-gold-500/40 transition-all duration-500 hover:shadow-[0_20px_80px_-20px_rgba(212,149,14,0.3)] flex flex-col lg:flex-row relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gold-500/0 via-gold-500/0 to-gold-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                
                {/* Featured Cover */}
                <div
                  className="lg:w-1/2 h-72 lg:h-auto relative overflow-hidden flex-shrink-0"
                  style={{ background: featuredPost.coverColor || COVER_COLORS[0] }}
                >
                  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white via-transparent to-transparent mix-blend-overlay" />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" />
                  
                  <div className="absolute top-6 left-6 flex gap-2">
                    <span className="bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-lg">
                      Pilihan Editor
                    </span>
                    <span className="bg-gold-500 text-navy-950 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                      {featuredPost.category}
                    </span>
                  </div>
                </div>

                {/* Featured Content */}
                <div className="flex-1 p-8 sm:p-12 lg:p-16 flex flex-col justify-center relative z-10 backdrop-blur-xl bg-navy-950/40">
                  <div className="flex items-center gap-4 text-sm text-navy-300 font-medium mb-6">
                    <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1 rounded-full border border-white/5">
                      <Clock size={14} className="text-gold-400" />
                      {featuredPost.readTime}
                    </span>
                    <span>•</span>
                    <span>{new Date(featuredPost.publishedAt).toLocaleDateString('id-ID', { day:'numeric', month:'long', year:'numeric' })}</span>
                  </div>
                  
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-[1.2] group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gold-200 transition-all duration-300">
                    {featuredPost.title}
                  </h2>
                  
                  <p className="text-navy-200/80 text-base sm:text-lg leading-relaxed mb-10 line-clamp-3 font-light">
                    {featuredPost.excerpt}
                  </p>
                  
                  <div className="mt-auto inline-flex items-center gap-3 text-gold-400 font-semibold group-hover:text-gold-300 transition-colors">
                    <span className="relative overflow-hidden">
                      <span className="block transition-transform duration-300 group-hover:-translate-y-full">Baca Artikel Lengkap</span>
                      <span className="block absolute inset-0 transition-transform duration-300 translate-y-full group-hover:translate-y-0">Baca Artikel Lengkap</span>
                    </span>
                    <div className="w-8 h-8 rounded-full bg-gold-500/10 flex items-center justify-center group-hover:bg-gold-500 group-hover:text-navy-950 transition-all duration-300">
                      <ArrowRight size={16} className="transform group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Rest Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {restPosts.map((post, i) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  className="group cursor-pointer rounded-3xl overflow-hidden bg-white/[0.02] border border-white/5 hover:border-gold-500/30 hover:bg-white/[0.04] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_rgba(212,149,14,0.15)] flex flex-col"
                >
                  {/* Cover */}
                  <div
                    className="h-56 flex items-center justify-center relative overflow-hidden flex-shrink-0"
                    style={{ background: post.coverColor || COVER_COLORS[i % COVER_COLORS.length] }}
                  >
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" />
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white via-transparent to-transparent mix-blend-overlay" />
                    <BookOpen size={48} className="text-white/10 transform group-hover:scale-110 transition-transform duration-700 drop-shadow-xl" />
                    
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-sm">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 sm:p-8 flex flex-col flex-1 relative">
                    <div className="flex items-center gap-3 text-xs text-navy-400 font-medium mb-4">
                      <span className="flex items-center gap-1.5">
                        <Clock size={12} className="text-gold-500" /> {post.readTime}
                      </span>
                      <span>•</span>
                      <span>{new Date(post.publishedAt).toLocaleDateString('id-ID', { day:'numeric', month:'short', year:'numeric' })}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3 leading-[1.3] group-hover:text-gold-300 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    
                    <p className="text-navy-300/80 text-sm leading-relaxed line-clamp-3 mb-6 font-light">
                      {post.excerpt}
                    </p>
                    
                    <div className="mt-auto pt-5 border-t border-white/5 flex items-center justify-between">
                      <span className="text-gold-400 text-sm font-semibold group-hover:text-gold-300 transition-colors">
                        Mulai Membaca
                      </span>
                      <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:border-gold-400 group-hover:bg-gold-500/10 transition-all duration-300">
                        <ArrowRight size={14} className="text-white group-hover:text-gold-400 transform group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
